// domain_status_view — PURE. Ce que le propriétaire lit sur son domaine.
//
// ── CE QUE CE MODULE NE FAIT PAS ────────────────────────────────────────────
//
// Il ne VALIDE PAS un domaine saisi. Cette règle vit côté serveur
// (platform-service/lib/custom_domain.ts) et rend déjà un message écrit pour le client. La recopier
// ici ferait DEUX vérités sur ce qu'est un domaine acceptable, et le jour où l'une bougerait,
// l'écran dirait oui à ce que le serveur refuse. L'écran affiche le refus du serveur, point.
//
// ── CE QUE JE N'INVENTE PAS ─────────────────────────────────────────────────
//
// Le fournisseur publie une liste d'états que je n'ai pas lue en entier. Je ne nomme donc que ceux
// que j'ai VUS dans sa référence d'API — `active` et `pending_validation` — et tout le reste est
// traité génériquement : « vérification en cours », avec les valeurs brutes affichées pour le
// support. Deviner un état produirait une phrase rassurante sur une situation qu'on ne comprend pas.

/** Les trois seules situations qu'un propriétaire a besoin de distinguer, et les seuls tons que la
 *  feuille de style connaît. */
export const DOMAIN_TONES = ["live", "waiting", "problem"] as const;
export type DomainTone = (typeof DOMAIN_TONES)[number];

export type DomainStatusInput = {
  /** L'état du NOM chez le fournisseur. */
  readonly status: string;
  /** L'état de son CERTIFICAT. */
  readonly ssl_status: string;
  /** Ce que le serveur a conclu des deux. Jamais cru seul — voir plus bas. */
  readonly live?: boolean;
  /** Pourquoi l'état n'a pas pu être lu, s'il n'a pas pu l'être. */
  readonly note?: string;
};

export type DomainStatusView = {
  readonly tone: DomainTone;
  readonly label: string;
  readonly detail?: string;
};

/**
 * PURE. Traduire l'état d'un domaine en une phrase et un ton.
 *
 * L'ordre des cas est la décision : une NOTE d'échec l'emporte sur tout, parce qu'annoncer
 * « en attente » quand on n'a pas pu lire l'état ferait attendre le client pour rien.
 *
 * « En ligne » exige les DEUX états actifs, et n'est jamais annoncé sur la seule foi de `live` :
 * l'écran montre ces deux valeurs juste à côté, et il ne doit pas se contredire lui-même.
 */
export function domainStatusView(d: DomainStatusInput): DomainStatusView {
  if (d.note) {
    return { tone: "problem", label: "état indisponible", detail: d.note };
  }
  if (d.status === "active" && d.ssl_status === "active") {
    return { tone: "live", label: "en ligne, en HTTPS" };
  }
  // LE CODE DE VALIDATION N'EXISTE PAS ENCORE. Mesuré le 2026-09-30 : à la création, le fournisseur
  // répond `ssl.status: "initializing"` avec une liste de validation VIDE. Réclamer « créez cette
  // ligne » à ce moment-là fait copier au client une valeur absente ou périmée — et la validation
  // attend ensuite un code qu'il n'a jamais publié, sans erreur, donc sans explication. C'est
  // exactement ce qui est arrivé au premier essai réel.
  if (d.ssl_status === "initializing") {
    return {
      tone: "waiting",
      label: "préparation du certificat",
      detail: "les lignes à créer apparaissent dans quelques secondes — cet écran se met à jour tout seul",
    };
  }
  if (d.ssl_status === "pending_validation") {
    return {
      tone: "waiting",
      label: "en attente de vos enregistrements DNS",
      detail: "créez les lignes ci-dessous chez votre registrar ; la vérification se fait ensuite toute seule",
    };
  }
  // Tout le reste : on ne prétend pas savoir. Les valeurs brutes sont montrées parce qu'elles sont
  // la seule chose utile au support le jour où un état imprévu arrive.
  return {
    tone: "waiting",
    label: "vérification en cours",
    detail: `nom : ${d.status || "inconnu"} · certificat : ${d.ssl_status || "inconnu"}`,
  };
}


/** Toutes les combien de millisecondes l'écran relit l'état.
 *
 *  Le code de validation arrive quelques SECONDES après l'ajout, et l'état final quelques minutes
 *  plus tard. Une seconde martèlerait le serveur et le fournisseur ; une minute ferait croire que
 *  rien ne bouge. Cinq secondes tiennent les deux. */
export const DOMAIN_POLL_MS = 5_000;

/**
 * PURE. Faut-il relire l'état, et dans combien de temps ? `null` = il n'y a plus rien à attendre.
 *
 * LE DÉFAUT QUE CECI FERME, mesuré au premier essai réel : l'écran ne relisait qu'au montage et
 * après une action. Le code de validation arrivait quelques secondes plus tard, et personne ne le
 * voyait — l'opérateur restait devant « en attente » en croyant que rien ne s'était passé, alors que
 * tout avançait.
 *
 * On s'ARRÊTE quand tous les domaines sont prêts : interroger pour toujours coûte des requêtes à
 * vide, chez nous et chez le fournisseur, pour une information qui ne changera plus.
 */
export function pollingDelayMs(domains: readonly DomainStatusInput[]): number | null {
  const enCours = domains.some((d) => !(d.status === "active" && d.ssl_status === "active"));
  return enCours ? DOMAIN_POLL_MS : null;
}
